import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { fmtDate } from '../format';
import type { Overview as O } from '../types';
import { ErrorNote, Icon, Loading, Pill, Section } from '../components/ui';

const STAGES: [string, string, string][] = [
  ['candidate', 'Candidate', 'Initial suitability'], ['prestart', 'Pre-start', 'Initial suitability'], ['probation', 'Probation', 'Ongoing suitability'],
  ['active', 'Active', 'Ongoing suitability'], ['separating', 'Leaving', 'Separation'],
];

export function Overview() {
  const q = useQuery({ queryKey: ['overview'], queryFn: () => api.get<O>('/api/overview'), refetchInterval: 60_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const o = q.data!;
  return (
    <div className="page">
      <div className="page-h"><div><p className="eyebrow">{fmtDate(o.today)}</p><h1>Staff lifecycle overview</h1></div></div>
      <Section title="Where people are in the lifecycle" aside={<span className="dim small">AS4811:2022 personnel risk lifecycle</span>}>
        <div className="pipeline">
          {STAGES.map(([k, label, phase], i) => (
            <Link key={k} to="/people" className="pipe">
              <span className={`pipe-phase${i > 0 && STAGES[i - 1]![2] === phase ? ' ghost' : ''}`}>{phase}</span>
              <span className="pipe-n">{o.stages[k] ?? 0}</span><span className="pipe-l">{label}</span>
            </Link>
          ))}
        </div>
      </Section>
      <div className="grid-2">
        <Section title="Needs action" className="hazard" aside={<Pill tone={o.counts.overdue ? 'bad' : 'ok'}>{o.counts.overdue} overdue</Pill>}>
          {o.alerts.length === 0 ? <p className="empty">Nothing overdue or due today.</p> : (
            <ul className="alerts">
              {o.alerts.map((a, i) => (
                <li key={i}><Link className="alert" to={a.link}>
                  <span className={`sev sev-${a.severity}`} /><span className="alert-body"><strong>{a.title}</strong><span>{a.detail}</span></span><span className="alert-cat">{a.category}</span>
                </Link></li>
              ))}
            </ul>
          )}
        </Section>
        <div className="stack">
          <Section title="Onboarding & offboarding" aside={<Link className="link" to="/checklists">Open checklists <Icon n="arrow" s={14} /></Link>}>
            <div className="tiles">
              <div className="tile"><span className="tile-n">{o.counts.openChecklists}</span><span>Checklists running</span></div>
              <div className="tile"><span className="tile-n tone-bad">{o.counts.overdue}</span><span>Tasks overdue</span></div>
              <div className="tile"><span className="tile-n tone-warn">{o.counts.dueThisWeek}</span><span>Due in the next 7 days</span></div>
            </div>
          </Section>
          <Section title="Documents" aside={<Link className="link" to="/documents">Open documents <Icon n="arrow" s={14} /></Link>}>
            <p><strong className="tile-n tone-warn">{o.counts.awaitingSignature}</strong> <span className="small">awaiting signature</span></p>
          </Section>
        </div>
      </div>
    </div>
  );
}
